import React, { useEffect, useMemo, useState } from 'react';
import omit from 'lodash/omit';
import { useSelector } from 'react-redux';
import { getCategoryIdByExternalId } from '@yme/api';
import { FacetId, ProductType, VARIANTS_ATTR_USAGE } from '@yme/common/constants';
import { AEM_FUNCTIONS } from '@yme/common/index';
import { IndentWrapper } from '@yme/components/Separator';
import { useWindowSize } from '@yme/hooks/index';
import { OverlapContainer } from '@yme/molecules/overlap-container';
import { usePageType } from '@yme/pages/components/ProductsGrid/usePageType';
import { getIs35kwFilterDisabled, getPageBlockStrategy } from '@yme/selectors/common';
import { colorCoreBlack900Base } from '@yme/tokens/colors';
import { ACCESSORY_FACETS_PLP, APPAREL_FACETS_PLP, FacetFields, } from '@yme/utils/bloomreach/constants';
import { selectColorTheme } from '@yme/utils/common';
import { COMMON_ANCHOR_ID_SCROLL_TO } from '@yme/utils/constants';
import { AccessoriesFilter, ComparisonFilter, CompatibilityPanel, AccessoriesFilterBloomreach, } from './components';
import { CLP_UNIT_SORTING, PLP_UNIT_SORTING, PLP_ACCESSORIES_SORTING, PLP_APPAREL_SORTING, PerPageLimit, PLP_ACCESSORIES_BLOOMREACH_SORTING, PLP_APPAREL_BLOOMREACH_SORTING, } from './components/common/constants';
import { getUnitFacetsConfig } from './components/common/helpers';
import { SearchDataSource } from './constants';
import { getFilterFacetsConfig } from './helpers';
import { useCompatibleProduct } from './useCompatibleProduct';
import { useDiscountedProducts } from './useDiscountedProducts';
export function ProductsGrid(props) {
    const { cqType, hyperdriveEndpoints, comparisonPagePath, isRemoveBottomIndent, selectModelModalModel, header = '', pdpPageUrl, productType, categoryModel, defaultStartYear, searchDataSource, isDiscountMode, isUsageFilterEnabled, isInEditor, categoryId, } = props;
    const [categoryIds, setCategoryIds] = useState([]);
    const pageBlockStrategy = useSelector(getPageBlockStrategy) || colorCoreBlack900Base;
    const { isMobileView } = useWindowSize();
    usePageType(cqType);
    const isSearchDataSourceBloomreach = searchDataSource === SearchDataSource.BLOOMREACH;
    const isAccessoriesOrApparel = [
        AEM_FUNCTIONS.plpAccessoriesGrid,
        AEM_FUNCTIONS.plpClothingMerchGrid,
    ].includes(cqType ?? '');
    const hasDiscountedProducts = useDiscountedProducts(hyperdriveEndpoints, !(isSearchDataSourceBloomreach && isAccessoriesOrApparel));
    const is35kwFilterDisabled = useSelector(getIs35kwFilterDisabled);
    const unitsFacetsConfig = useMemo(() => getUnitFacetsConfig(productType, {
        is35kwFilterDisabled,
    }), [productType, is35kwFilterDisabled]);
    const accessoriesFacetsConfig = useMemo(() => {
        const result = isSearchDataSourceBloomreach
            ? [...ACCESSORY_FACETS_PLP]
            : getFilterFacetsConfig(FacetId.ACCESSORIES);
        if (isUsageFilterEnabled) {
            // prepend usage filter to the facets list
            result.unshift(isSearchDataSourceBloomreach ? FacetFields.ACCESSORY_USAGE : VARIANTS_ATTR_USAGE);
        }
        return result;
    }, [isSearchDataSourceBloomreach, isUsageFilterEnabled]);
    const clothingMerchandiseFacetsConfig = useMemo(() => isSearchDataSourceBloomreach
        ? APPAREL_FACETS_PLP
        : getFilterFacetsConfig(FacetId.CLOTHING_MERCHANDISE), [isSearchDataSourceBloomreach]);
    const compatiblePanelAPI = useCompatibleProduct({
        endpoint: hyperdriveEndpoints.products,
        isEnabled: selectModelModalModel?.showCompatibilityCheck || false,
    });
    const isCategoryIdsLoaded = categoryIds?.length;
    const internalCategoryIds = isCategoryIdsLoaded ? categoryIds : [];
    const AccessoriesFilterComponent = isSearchDataSourceBloomreach
        ? AccessoriesFilterBloomreach
        : AccessoriesFilter;
    // Get categoryId by ExternalId
    useEffect(() => {
        (async () => {
            try {
                if (hyperdriveEndpoints?.categoryInfo &&
                    !(isSearchDataSourceBloomreach && isAccessoriesOrApparel)) {
                    const currentCategoryIds = await getCategoryIdByExternalId({
                        endpoint: hyperdriveEndpoints.categoryInfo,
                    });
                    if (currentCategoryIds) {
                        setCategoryIds(currentCategoryIds);
                    }
                }
            }
            catch (err) { }
        })();
    }, [hyperdriveEndpoints.categoryInfo, isAccessoriesOrApparel, isSearchDataSourceBloomreach]);
    if (cqType === AEM_FUNCTIONS.plpUnitsGrid) {
        return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent, dataTestId: "plpUnitsGrid" },
            React.createElement(OverlapContainer, { borderSize: isMobileView ? '4px' : '6px', brandedColor: selectColorTheme(pageBlockStrategy), hasVerticalWhiteGradient: true, isDefaultVerticalPadding: true },
                React.createElement(ComparisonFilter, { isInEditor: isInEditor, title: header, filterSettings: {
                        isPaginationExist: true,
                        perPageLimitSetting: PerPageLimit.DESKTOP_UNITS_PLP,
                        sortOptionsType: PLP_UNIT_SORTING,
                    }, endpoints: omit(hyperdriveEndpoints, ['products']), categoryModel: categoryModel, pdpPageUrl: pdpPageUrl, defaultStartYear: defaultStartYear, cqType: cqType, productEndpoint: hyperdriveEndpoints?.products, comparisonPagePath: comparisonPagePath, scrollToComponentId: COMMON_ANCHOR_ID_SCROLL_TO, categoryIds: internalCategoryIds, filterFacetsConfig: unitsFacetsConfig }))));
    }
    if (cqType === AEM_FUNCTIONS.plpAccessoriesGrid) {
        return (React.createElement(React.Fragment, null,
            selectModelModalModel &&
                selectModelModalModel.showCompatibilityCheck &&
                !isSearchDataSourceBloomreach && (React.createElement(CompatibilityPanel, { compatiblePanelAPI: compatiblePanelAPI, selectModelProps: selectModelModalModel, dataTestId: "CompatibilityPanel_plpAccessoriesGrid" })),
            React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent, dataTestId: "plpAccessoriesGrid" },
                React.createElement(AccessoriesFilterComponent, { isInEditor: isInEditor, filterSettings: {
                        perPageLimitSetting: PerPageLimit.DESKTOP_PLP_ACCESSORIES,
                        sortOptionsType: isSearchDataSourceBloomreach
                            ? PLP_ACCESSORIES_BLOOMREACH_SORTING
                            : PLP_ACCESSORIES_SORTING,
                    }, cqType: cqType, categoryIds: internalCategoryIds, productEndpoint: hyperdriveEndpoints.products, productType: ProductType.ACCESSORY, pdpPageUrl: pdpPageUrl, filterFacetsConfig: accessoriesFacetsConfig, compatiblePanelAPI: compatiblePanelAPI, isDiscountMode: isDiscountMode, hasDiscountedProducts: hasDiscountedProducts, categoryId: categoryId }))));
    }
    if (cqType === AEM_FUNCTIONS.plpClothingMerchGrid) {
        return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent, dataTestId: "plpClothingMerchGrid" },
            React.createElement(AccessoriesFilterComponent, { isInEditor: isInEditor, filterSettings: {
                    sortOptionsType: isSearchDataSourceBloomreach
                        ? PLP_APPAREL_BLOOMREACH_SORTING
                        : PLP_APPAREL_SORTING,
                    perPageLimitSetting: PerPageLimit.DESKTOP_PLP_APPAREL,
                }, cqType: cqType, categoryIds: internalCategoryIds, productType: ProductType.APPAREL, productEndpoint: hyperdriveEndpoints.products, pdpPageUrl: pdpPageUrl, filterFacetsConfig: clothingMerchandiseFacetsConfig, isDiscountMode: isDiscountMode, hasDiscountedProducts: hasDiscountedProducts, categoryId: categoryId })));
    }
    if (cqType === AEM_FUNCTIONS.clpUnitsGrid) {
        return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent, dataTestId: "clpUnitsGrid" },
            React.createElement(ComparisonFilter, { isInEditor: isInEditor, filterSettings: {
                    isPaginationExist: true,
                    perPageLimitSetting: PerPageLimit.DESKTOP_UNITS_CLP,
                    sortOptionsType: CLP_UNIT_SORTING,
                }, endpoints: omit(hyperdriveEndpoints, ['products']), categoryModel: categoryModel, pdpPageUrl: pdpPageUrl, defaultStartYear: defaultStartYear, title: header, cqType: cqType, productEndpoint: hyperdriveEndpoints.products, scrollToComponentId: COMMON_ANCHOR_ID_SCROLL_TO, comparisonPagePath: comparisonPagePath, categoryIds: internalCategoryIds, filterFacetsConfig: unitsFacetsConfig })));
    }
    return null;
}
ProductsGrid.displayName = 'ProductsGrid_AEMComponent';
